export type SourceAttachmentPurpose = 'feedback' | 'community' | 'ai';

export const MAX_SOURCE_ATTACHMENTS = 3;
export const MAX_SOURCE_LINKS = 3;
export const MAX_SOURCE_ATTACHMENT_BYTES_BY_PURPOSE: Readonly<Record<SourceAttachmentPurpose, number>> = {
	feedback: 5 * 1024 * 1024,
	community: 30 * 1024 * 1024,
	ai: 30 * 1024 * 1024,
};
export const SOURCE_INPUT_REQUIRED_ERROR = 'Add at least one link or file.';
export const SOURCE_INPUT_UPLOAD_HINT = 'Add at least one link or file. Up to 3 links and 3 files. Supported files: .doc, .docx, .pdf, .txt, .md, .markdown';
export const SOURCE_DOCUMENT_ACCEPT = '.doc,.docx,.pdf,.txt,.md,.markdown';

type SourceAttachmentPolicy = {
	extensions: readonly string[];
	mimeByExtension: Readonly<Record<string, string>>;
};

const SOURCE_DOCUMENT_POLICY = {
	extensions: ['doc', 'docx', 'pdf', 'txt', 'md', 'markdown'],
	mimeByExtension: {
		doc: 'application/msword',
		docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		pdf: 'application/pdf',
		txt: 'text/plain',
		md: 'text/plain',
		markdown: 'text/plain',
	},
} as const satisfies SourceAttachmentPolicy;

const FEEDBACK_IMAGE_POLICY = {
	extensions: ['png', 'jpg', 'jpeg', 'webp'],
	mimeByExtension: {
		png: 'image/png',
		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		webp: 'image/webp',
	},
} as const satisfies SourceAttachmentPolicy;

export function getSourceAttachmentPolicy(purpose: SourceAttachmentPurpose): SourceAttachmentPolicy {
	return purpose === 'feedback' ? FEEDBACK_IMAGE_POLICY : SOURCE_DOCUMENT_POLICY;
}

export function maxSourceAttachmentBytes(purpose: SourceAttachmentPurpose): number {
	return MAX_SOURCE_ATTACHMENT_BYTES_BY_PURPOSE[purpose];
}

function lastExtension(name: string | undefined): string | null {
	const trimmed = name?.trim() ?? '';
	const index = trimmed.lastIndexOf('.');
	if (index <= 0 || index === trimmed.length - 1) return null;
	return trimmed.slice(index + 1).toLowerCase();
}

function normalizeReportedMime(type: string | undefined): string {
	const reported = type?.trim().toLowerCase() ?? '';
	if (reported === 'application/octet-stream' || reported === 'binary/octet-stream') return '';
	return reported === 'image/jpg' ? 'image/jpeg' : reported;
}

function isMarkdownExtension(extension: string): boolean {
	return extension === 'md' || extension === 'markdown';
}

/**
 * Resolve the sole MIME type authorized for this filename and purpose.
 * Extension is authoritative so a `report.pdf.exe` cannot borrow PDF MIME.
 */
export function resolveSourceAttachmentMime(purpose: SourceAttachmentPurpose, file: { name?: string; type?: string }): string | null {
	const extension = lastExtension(file.name);
	if (!extension) return null;
	const expected = getSourceAttachmentPolicy(purpose).mimeByExtension[extension];
	if (!expected) return null;
	const reported = normalizeReportedMime(file.type);
	if (isMarkdownExtension(extension)) {
		return reported === '' || reported === 'text/plain' || reported === 'text/markdown' || reported === 'text/x-markdown' ? 'text/plain' : null;
	}
	return reported === '' || reported === expected ? expected : null;
}

export function validateSourceAttachment(
	purpose: SourceAttachmentPurpose,
	file: { name?: string; type?: string; size?: number },
): { ok: true; contentType: string } | { ok: false; reason: 'type' | 'size' } {
	const contentType = resolveSourceAttachmentMime(purpose, file);
	if (!contentType) return { ok: false, reason: 'type' };
	const size = file.size ?? 0;
	if (size <= 0 || size > maxSourceAttachmentBytes(purpose)) return { ok: false, reason: 'size' };
	return { ok: true, contentType };
}

export function isSourceAttachmentSizeValid(purpose: SourceAttachmentPurpose, size: number): boolean {
	return Number.isInteger(size) && size > 0 && size <= maxSourceAttachmentBytes(purpose);
}

/** Metadata arrives from a completed receipt, so it must already be canonical. */
export function isSourceAttachmentMetadataValid(purpose: Exclude<SourceAttachmentPurpose, 'feedback'>, attachment: { name: string; contentType: string }): boolean {
	const normalized = resolveSourceAttachmentMime(purpose, attachment);
	return normalized !== null && normalized === attachment.contentType.trim().toLowerCase();
}

export function hasCompletedSourceInput(
	links: readonly unknown[] | undefined,
	attachments: readonly unknown[] | undefined,
): boolean {
	return (links?.length ?? 0) > 0 || (attachments?.length ?? 0) > 0;
}
